import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useDebounce } from "../../hooks/useDebounce";
import { useFilterSearch } from "../../hooks/useFilterSearch";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchConfiguration } from "../../http/types/configurations";
import { useConfigurationsApi } from "../../http/useConfigurations";
import { useProjectsApi } from "../../http/useProjects";
import { PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import SvgCopy from "../icons/Copy";
import SvgEdit from "../icons/Edit";
import SvgExcel from "../icons/Excel";
import SvgVisibility from "../icons/Visibility";
import Button from "../ui/Button";
import FlexContent from "../ui/FlexContent";
import { Action, getDeleteAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SectionCard from "../ui/SectionCard";
import Table from "../ui/Table";
import TableHeading from "../ui/TableHeading";
import CreateConfigurationButton from "./CreateConfigurationButton";
import DuplicateConfigurationDialog from "./DuplicateConfigurationDialog";

interface ProjectConfigurationsProps {
  projectId: string;
  isAdmin?: boolean;
  disabled?: boolean;
  canEdit?: boolean;
}

const ProjectConfigurations: FC<ProjectConfigurationsProps> = ({
  projectId,
  isAdmin,
  disabled,
  canEdit,
}: ProjectConfigurationsProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const projectsApi = useProjectsApi();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "projectConfigurations");
  const configurationsApi = useConfigurationsApi();

  const debouncedValue = useDebounce(searchTerm);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = configurationsApi.useProjectConfigurations(projectId, query, aborter.current.signal, isAdmin);

  const [configurationToDelete, setConfigurationToDelete] = useState<SearchConfiguration>();
  const [configurationToDuplicate, setConfigurationToDuplicate] = useState<SearchConfiguration>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rowActions: (item: SearchConfiguration) => Action[] = (item) => [
    {
      title: isAdmin || canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
      onClick: () => navigate(`${isAdmin ? "/admin" : ""}/projects/${projectId}/configurations/${item.id}`),
      icon: isAdmin || canEdit ? SvgEdit : SvgVisibility,
    },
    {
      title: i18n.translation.common.duplicate,
      onClick: () => {
        (canEdit || isAdmin) && setConfigurationToDuplicate(item);
        return !(canEdit || isAdmin);
      },
      isDisabled: !isAdmin && !canEdit,
      icon: SvgCopy,
    },
    getDeleteAction(
      i18n,
      () => {
        (canEdit || isAdmin) && setConfigurationToDelete(item);
        return !(canEdit || isAdmin);
      },
      true,
      !isAdmin && !canEdit,
    ),
  ];

  const buttons = [
    <Button
      glyph={SvgExcel}
      type="secondary"
      buttonProps={{
        disabled: disabled || !!error || !data?.items[0],
        onClick: () =>
          projectsApi
            .downloadEServiceExportConfigurationsItemsList(projectId, isAdmin)
            .catch((err) => dispatch(showErrorNotification(i18n, err))),
      }}
    >
      {i18n.translation.common.excel}
    </Button>,
  ];

  if (isAdmin || canEdit) {
    buttons.push(
      <CreateConfigurationButton
        projectId={projectId}
        disabled={disabled || (!isAdmin && !canEdit)}
        type="project"
        title={i18n.translation.common.createNew}
      />,
    );
  }

  return (
    <>
      <FlexContent padding noPaddingTop>
        <SectionCard>
          <TableHeading
            isSearchable
            searchValue={searchTerm}
            searchPlaceholder={i18n.translation.configurations.search}
            searchDisabled={disabled}
            searchButtons={buttons}
            onSearchChange={handleSearchChange}
          />
          <Table<SearchConfiguration>
            filters={filters}
            isTall
            head={
              <tr>
                <th align="left">{i18n.translation.common.name}</th>
                <th align="left">{i18n.translation.systems.singular}</th>
                <th align="left">{i18n.translation.common.author}</th>
                <th align="left">{i18n.translation.common.lastModified}</th>
                <th></th>
              </tr>
            }
            items={data?.items}
            renderRow={(item) => (
              <>
                <td>
                  <Link
                    to={`${isAdmin ? "/admin" : ""}/projects/${projectId}/configurations/${item.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.name}
                  </Link>
                </td>
                <td>{item.system?.name}</td>
                <td>
                  {item.author?.firstName} {item.author?.lastName}
                </td>
                <td>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</td>
              </>
            )}
            onRowClick={(item) =>
              navigate(`${isAdmin ? "/admin" : ""}/projects/${projectId}/configurations/${item.id}`)
            }
            rowActions={rowActions}
            total={data?.total}
            error={!!error}
          />
        </SectionCard>
        {!!configurationToDelete && (
          <ModalDialog
            onClose={() => setConfigurationToDelete(undefined)}
            title={i18n.translation.configurations.delete}
            isSubmitting={isSubmitting}
            actions={[
              {
                title: i18n.translation.common.cancel,
                onClick: () => setConfigurationToDelete(undefined),
                type: "secondary",
              },
              {
                title: i18n.translation.common.yes,
                onClick: () => {
                  setIsSubmitting(true);
                  configurationsApi
                    .deleteConfiguration(configurationToDelete.project?.id, configurationToDelete.id)
                    .then(() => setConfigurationToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                },
              },
            ]}
          >
            {i18n.translation.configurations.questions.delete}
          </ModalDialog>
        )}
        {!!configurationToDuplicate && (
          <DuplicateConfigurationDialog
            configuration={configurationToDuplicate}
            isAdmin={isAdmin}
            onClose={() => setConfigurationToDuplicate(undefined)}
            onSave={() => setConfigurationToDuplicate(undefined)}
          />
        )}
      </FlexContent>
    </>
  );
};

export default ProjectConfigurations;
